import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useDropzone, type Accept } from "react-dropzone";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Alert,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Input,
  Pill,
  ProgressBar,
  CircularProgress,
  FinanceableLogo,
} from "@financeable-com-au/financeable-ui";
import {
  ALLOWED_ASSET_DOCUMENT_TYPE,
  ALLOWED_DOCUMENT_TYPE,
  ERRORS_MESSAGE,
  FILE_ACCEPTANCE_TYPES,
  MAX_DOCUMENT_UPLOAD_SIZE,
  ROUTES,
  SECTION_TYPE,
  SUCCESS_MESSAGE,
} from "@/constants";
import {
  deleteDocument,
  getDocumentRequestProgress,
  removeAssetInvoice,
  submitDocuments,
  uploadAssetInvoice,
  uploadDocument,
  // @ts-expect-error: ignoring as it is an existing js file
} from "@/store/slices/applicationFormSlice";
// @ts-expect-error: ignoring as it is an existing js file
import { getCustomerById } from "@/store/slices/customerSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";

/** A document the broker has asked the recipient to provide, plus its upload state. */
interface RequestedDocument {
  key: string;
  name: string;
  description: string;
  uploaded: boolean;
  document: { _id?: string; fileName?: string; url?: string } | null;
  /** UI-only: marks docs the customer adds themselves under "something else to share". */
  custom?: boolean;
}

interface DocumentRequestProgress {
  customerId?: string;
  assetId?: string;
  applicationId: string;
  applicationType: string;
  requestedDocuments: RequestedDocument[];
  uploadedCount: number;
  totalCount: number;
  isComplete: boolean;
}

type DocumentRequestEntityType = "customer" | "asset";

interface DocumentRequestUploadProps {
  entityType: DocumentRequestEntityType;
}

interface DocumentRequestConfig {
  formDataIdKey: "customerId" | "assetId";
  idParamNames: string[];
  frontendPath: string;
  loadingText: string;
  introText: string;
}

const DOCUMENT_REQUEST_CONFIG: Record<DocumentRequestEntityType, DocumentRequestConfig> = {
  customer: {
    formDataIdKey: "customerId",
    idParamNames: ["customerId", "cid"],
    frontendPath: ROUTES.CUSTOMER_UPLOAD_DOCUMENTS,
    loadingText: "Loading your document request…",
    introText:
      "We need a few documents to finalise your application. Upload them below — it only takes a couple of minutes.",
  },
  asset: {
    formDataIdKey: "assetId",
    idParamNames: ["assetId", "asid"],
    frontendPath: ROUTES.ASSET_UPLOAD_DOCUMENTS,
    loadingText: "Loading your asset document request…",
    introText:
      "We need a few asset documents to finalise your application. Upload them below — it only takes a couple of minutes.",
  },
};

const FILE_ACCEPT: Accept = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const ASSET_INVOICE_MAX_DOCUMENT_UPLOAD_SIZE = 5 * 1024 * 1024;

const DOCUMENT_REQUEST_FILE_CONFIG: Record<
  DocumentRequestEntityType,
  {
    accept: Accept;
    allowedFileTypes: string[];
    maxFileSize: number;
    helperText: string;
    fileTypeErrorMessage: string;
    fileSizeErrorMessage: string;
  }
> = {
  customer: {
    accept: FILE_ACCEPT,
    allowedFileTypes: ALLOWED_DOCUMENT_TYPE,
    maxFileSize: MAX_DOCUMENT_UPLOAD_SIZE,
    helperText: "PDF, JPG or PNG · tap or drag here",
    fileTypeErrorMessage: ERRORS_MESSAGE.documentTypeMsg,
    fileSizeErrorMessage: ERRORS_MESSAGE.documentSizeMsg,
  },
  asset: {
    accept: FILE_ACCEPTANCE_TYPES[SECTION_TYPE.ASSET_INVOICE] as unknown as Accept,
    allowedFileTypes: ALLOWED_ASSET_DOCUMENT_TYPE,
    maxFileSize: ASSET_INVOICE_MAX_DOCUMENT_UPLOAD_SIZE,
    helperText: "PDF or XLSX · tap or drag here",
    fileTypeErrorMessage: "File type must be pdf or xlsx only",
    fileSizeErrorMessage: "File size exceeds the limit (5 MB)",
  },
};

const CARD_WIDTH = 460;
// On desktop the shell widens into a two-pane layout instead of a phone-width card.
// Capped on very wide screens so line lengths stay readable while still filling space.
const SHELL_MAX_WIDTH = { xs: 480, md: 1100, lg: 1440, xl: 1600 };

const titleCase = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const getParamValue = (params: Record<string, string>, names: string[]) => {
  const paramName = names.find((name) => params[name]);
  return paramName ? params[paramName] : "";
};

/** Pick the customer's first name from the loaded record, falling back to the email handle. */
const resolveFirstName = (customer: { firstName?: string; emails?: { address?: string }[] } | null): string => {
  if (customer?.firstName) return customer.firstName;
  const email = customer?.emails?.slice(-1)[0]?.address;
  if (email) return titleCase(email.split("@")[0]);
  return "there";
};

const isAcceptedFile = (file: File, allowedFileTypes: string[], maxFileSize: number) =>
  allowedFileTypes.includes(file.type) && file.size <= maxFileSize;

interface DocumentRowProps {
  doc: RequestedDocument;
  isUploading: boolean;
  isDeleting: boolean;
  isDisabled: boolean;
  accept: Accept;
  allowedFileTypes: string[];
  maxFileSize: number;
  helperText: string;
  fileTypeErrorMessage: string;
  fileSizeErrorMessage: string;
  onUpload: (doc: RequestedDocument, file: File) => void;
  onDelete: (doc: RequestedDocument) => void;
}

const DocumentRow = ({
  doc,
  isUploading,
  isDeleting,
  isDisabled,
  accept,
  allowedFileTypes,
  maxFileSize,
  helperText,
  fileTypeErrorMessage,
  fileSizeErrorMessage,
  onUpload,
  onDelete,
}: DocumentRowProps) => {
  const dispatch = useDispatch();

  const onDrop = useCallback(
    (accepted: File[], rejected: { file: File }[]) => {
      const file = accepted[0];
      if (rejected.length || !file) {
        dispatch(openSnackbar({ message: fileTypeErrorMessage, intent: "danger" }));
        return;
      }
      if (!isAcceptedFile(file, allowedFileTypes, maxFileSize)) {
        dispatch(
          openSnackbar({
            message: file.size > maxFileSize ? fileSizeErrorMessage : fileTypeErrorMessage,
            intent: "danger",
          }),
        );
        return;
      }
      onUpload(doc, file);
    },
    [allowedFileTypes, dispatch, doc, fileSizeErrorMessage, fileTypeErrorMessage, maxFileSize, onUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    disabled: isDisabled || isUploading,
    noKeyboard: true,
  });

  let uploadLabel = "Upload file";
  if (isUploading) uploadLabel = "Uploading…";
  else if (isDragActive) uploadLabel = "Drop your file here";

  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: "1px solid",
        borderColor: doc.uploaded ? "success.outlinedBorder" : "neutral.outlinedBorder",
        bgcolor: "background.surface",
        overflow: "hidden",
      }}
    >
      {/* Title row */}
      <Stack direction="row" columnGap={1.5} alignItems="flex-start" sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: "9px",
            flexShrink: 0,
            bgcolor: doc.uploaded ? "success.softBg" : "primary.softBg",
            color: doc.uploaded ? "success.solidBg" : "primary.solidBg",
          }}
        >
          {doc.uploaded ? <CheckCircleRoundedIcon fontSize="small" /> : <DescriptionOutlinedIcon fontSize="small" />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-sm" bold>
            {doc.name}
          </Typography>
          {doc.description && (
            <Typography level="body-xs" textColor="text.tertiary">
              {doc.description}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Upload / uploaded zone */}
      <Box sx={{ px: 2, pb: 2 }}>
        {doc.uploaded ? (
          <Stack
            direction="row"
            alignItems="center"
            columnGap={1.25}
            sx={{
              borderRadius: "10px",
              bgcolor: "success.softBg",
              color: "success.softColor",
              px: 1.5,
              py: 1.25,
            }}
          >
            <Box sx={{ display: "flex", color: "success.solidBg" }}>
              <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography level="body-sm" bold noWrap>
                {doc.document?.fileName || "Uploaded"}
              </Typography>
            </Box>
            {doc.document?.url && (
              <IconButton
                variant="inline"
                size="xl"
                onClick={() => window.open(doc.document?.url, "_blank", "noopener,noreferrer")}
                aria-label={`View ${doc.name}`}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            )}
            <IconButton
              variant="inline"
              intent="danger"
              size="sm"
              loading={isDeleting}
              disabled={isDisabled || isUploading}
              onClick={() => onDelete(doc)}
              aria-label={`Remove ${doc.name}`}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Stack>
        ) : (
          <Box
            {...getRootProps()}
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: 1.5,
              cursor: isDisabled ? "default" : "pointer",
              borderRadius: "10px",
              border: "1px dashed",
              borderColor: isDragActive ? "primary.outlinedBorder" : "neutral.outlinedBorder",
              bgcolor: isDragActive ? "primary.softBg" : "background.level1",
              color: "text.tertiary",
              px: 1.5,
              py: 1.5,
              transition: "border-color 0.15s, background-color 0.15s",
              "&:hover": { borderColor: "primary.outlinedBorder", bgcolor: "primary.softBg" },
            }}
          >
            <input {...getInputProps()} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "8px",
                flexShrink: 0,
                bgcolor: "background.surface",
                color: "primary.solidBg",
              }}
            >
              {isUploading ? <CircularProgress size="sm" /> : <FileUploadOutlinedIcon sx={{ fontSize: "1.1rem" }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography level="body-sm" bold textColor="text.primary">
                {uploadLabel}
              </Typography>
              <Typography level="body-xs" textColor="text.tertiary">
                {helperText}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export function DocumentRequestUpload({ entityType }: DocumentRequestUploadProps) {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const config = DOCUMENT_REQUEST_CONFIG[entityType];
  const fileConfig = DOCUMENT_REQUEST_FILE_CONFIG[entityType];
  const showAdditionalDocuments = pathname !== ROUTES.ASSET_UPLOAD_DOCUMENTS;

  const params = useMemo(
    () => Object.fromEntries(new URLSearchParams(search).entries()) as Record<string, string>,
    [search],
  );
  const { token, jwt } = params;
  const urlEntityId =
    getParamValue(params, config.idParamNames) ||
    (entityType === "asset" && (params.applicationId || params.appId) ? params.aid || "" : "");
  const urlApplicationId = params.applicationId || params.appId || params.aid || "";

  const [progress, setProgress] = useState<DocumentRequestProgress | null>(null);
  const [customDocs, setCustomDocs] = useState<RequestedDocument[]>([]);
  const [firstName, setFirstName] = useState("there");
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  // Docs uploaded in this session, keyed by doc key. Overlaid onto the server
  // progress so a fresh upload shows as "uploaded" immediately, without waiting
  // for the backend to reflect it on the next progress fetch.
  const [localUploads, setLocalUploads] = useState<Record<string, { fileName: string }>>({});
  const [customLabel, setCustomLabel] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [customerAccess, setCustomerAccess] = useState<{
    entityId: string;
    jwt: string;
    forbidden: boolean;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const entityId = urlEntityId || progress?.[config.formDataIdKey] || "";
  const applicationId = urlApplicationId || progress?.applicationId || "";
  const shouldFetchCustomer = entityType === "customer" && !!entityId && !!jwt;
  const hasCheckedCustomer = customerAccess?.entityId === entityId && customerAccess?.jwt === jwt;
  const isForbidden = shouldFetchCustomer && hasCheckedCustomer && customerAccess?.forbidden;

  // The public request link carries a JWT; keep it in sessionStorage for helpers
  // that are still scoped to the customer session token.
  useEffect(() => {
    if (jwt) sessionStorage.setItem("token", jwt);
  }, [jwt]);

  const fetchProgress = useCallback(
    (showLoader = false) => {
      if (!token) {
        setIsLoading(false);
        setLoadError(true);
        return;
      }
      if (showLoader) setIsLoading(true);
      dispatch(getDocumentRequestProgress({ token, entityType, jwt })).then((res: IDispatchResponse) => {
        setIsLoading(false);
        const data = (res?.payload as { data?: { data?: DocumentRequestProgress } })?.data?.data;
        if (res?.error || !data) {
          setLoadError(true);
          return;
        }
        setLoadError(false);
        setProgress(data);
      });
    },
    [dispatch, entityType, jwt, token],
  );

  useEffect(() => {
    fetchProgress(true);
  }, [fetchProgress]);

  useEffect(() => {
    if (entityType !== "customer" || !entityId || !jwt) return;
    let active = true;
    dispatch(getCustomerById({ customerId: entityId, jwt })).then((res: IDispatchResponse) => {
      if (!active) return;
      setCustomerAccess({
        entityId,
        jwt,
        forbidden: !!res?.error && (res?.meta?.status === 403 || res?.meta?.status === 401),
      });
      if (res?.error) return;
      const customer = (res?.payload as { data?: { data?: unknown } })?.data?.data as Parameters<
        typeof resolveFirstName
      >[0];
      if (customer) setFirstName(resolveFirstName(customer));
    });
    return () => {
      active = false;
    };
  }, [dispatch, entityId, entityType, jwt]);

  const handle = {
    uploadDocument: (doc: RequestedDocument, file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      if (applicationId) formData.append("applicationId", applicationId);
      if (entityId) formData.append(config.formDataIdKey, entityId);
      formData.append("name", entityType === "asset" ? "invoice" : doc.name);
      formData.append("frontendPath", config.frontendPath);
      if (token) formData.append("documentRequestToken", token);

      setUploadingKey(doc.key);
      dispatch(entityType === "asset" ? uploadAssetInvoice({ formData, jwt }) : uploadDocument({ formData })).then(
        (res: IDispatchResponse) => {
          setUploadingKey(null);
          if (res?.error) {
            dispatch(
              openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
            );
            return;
          }
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.fileUploadSuccess,
              intent: "success",
            }),
          );
          // Reflect the upload right away; fetchProgress reconciles with the server.
          setLocalUploads((prev) => ({ ...prev, [doc.key]: { fileName: file.name } }));
          if (doc.custom) {
            setCustomDocs((prev) =>
              prev.map((item) =>
                item.key === doc.key ? { ...item, uploaded: true, document: { fileName: file.name } } : item,
              ),
            );
          }
          fetchProgress();
        },
      );
    },
    deleteDocument: (doc: RequestedDocument) => {
      const removeLocally = () => {
        setLocalUploads((prev) => {
          const next = { ...prev };
          delete next[doc.key];
          return next;
        });
        setCustomDocs((prev) => prev.filter((item) => item.key !== doc.key));
      };

      // Custom extras and optimistic rows have no persisted id yet — just drop them.
      if (!doc.document?._id) {
        removeLocally();
        return;
      }

      setDeletingKey(doc.key);
      const deleteAction =
        entityType === "asset"
          ? removeAssetInvoice({
              ...(entityId ? { assetId: entityId } : {}),
              ...(applicationId ? { applicationId } : {}),
              documentId: doc.document._id,
              jwt,
            })
          : deleteDocument({
              ...(entityId ? { [config.formDataIdKey]: entityId } : {}),
              ...(applicationId ? { applicationId } : {}),
              documentId: doc.document._id,
              ...(token ? { documentRequestToken: token } : {}),
            });

      dispatch(deleteAction).then((res: IDispatchResponse) => {
        setDeletingKey(null);
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
          return;
        }
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.fileRemovedSuccess,
            intent: "success",
          }),
        );
        removeLocally();
        fetchProgress();
      });
    },
    addCustomDocument: () => {
      const name = customLabel.trim();
      if (!name) return;
      setCustomDocs((prev) => [
        ...prev,
        { key: `custom-${Date.now()}`, name, description: "", uploaded: false, document: null, custom: true },
      ]);
      setCustomLabel("");
    },
    submitDocuments: () => {
      if (!token) return;
      setIsSubmitting(true);
      dispatch(submitDocuments({ token, entityType, jwt })).then((res: IDispatchResponse) => {
        setIsSubmitting(false);
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
          return;
        }
        setIsSubmitted(true);
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.documentsSubmitted,
            intent: "success",
          }),
        );
      });
    },
  };

  // Overlay this session's uploads so a just-uploaded doc reads as "uploaded"
  // even if the server progress fetch hasn't caught up yet.
  const applyLocalUploads = (docs: RequestedDocument[]): RequestedDocument[] =>
    docs.map((doc) =>
      localUploads[doc.key] && !doc.uploaded
        ? { ...doc, uploaded: true, document: doc.document ?? { fileName: localUploads[doc.key].fileName } }
        : doc,
    );

  // A row is busy while it (or, for shared disabling, another row) uploads or deletes.
  const busyKey = uploadingKey || deletingKey;
  const requestedDocuments = applyLocalUploads(progress?.requestedDocuments ?? []);
  const extraDocuments = applyLocalUploads(customDocs);
  const uploadedCount = requestedDocuments.filter((doc) => doc.uploaded).length;
  const totalCount = progress?.totalCount ?? requestedDocuments.length;
  const remaining = Math.max(totalCount - uploadedCount, 0);
  const percent = totalCount ? Math.round((uploadedCount / totalCount) * 100) : 0;
  const isComplete = totalCount > 0 && remaining === 0;

  const pageBox = (children: React.ReactNode) => (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.level1",
        display: "flex",
        justifyContent: "center",
        // Pin to top on mobile (content can be tall); on desktop stretch the card
        // so it fills the viewport instead of floating in the middle.
        alignItems: { xs: "flex-start", md: "stretch" },
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 3 },
      }}
    >
      {children}
    </Box>
  );

  if (isForbidden) {
    return pageBox(
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "60vh" }}>
        <Alert
          title="Oops, looks like you're lost!"
          description="You don't have permission to view this page."
          intent="danger"
          variant="filled"
        />
      </Stack>,
    );
  }

  if (isLoading || (shouldFetchCustomer && !hasCheckedCustomer)) {
    return pageBox(
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "60vh" }} rowGap={2}>
        <CircularProgress />
        <Typography level="body-sm" textColor="text.tertiary">
          {config.loadingText}
        </Typography>
      </Stack>,
    );
  }

  if (loadError) {
    return pageBox(
      <Box
        sx={{
          width: "100%",
          maxWidth: CARD_WIDTH,
          bgcolor: "background.surface",
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "neutral.outlinedBorder",
          p: 4,
          textAlign: "center",
        }}
      >
        <Stack rowGap={1.5} alignItems="center">
          <Box sx={{ display: "flex", color: "warning.solidBg" }}>
            <LockOutlinedIcon />
          </Box>
          <Typography level="title-md" bold>
            This link is no longer available
          </Typography>
          <Typography level="body-sm" textColor="text.tertiary">
            The document request may have expired or already been completed. Please contact your broker for a new link.
          </Typography>
        </Stack>
      </Box>,
    );
  }

  return pageBox(
    <Box
      sx={{
        width: "100%",
        maxWidth: SHELL_MAX_WIDTH,
        bgcolor: "background.surface",
        borderRadius: "20px",
        border: "1px solid",
        borderColor: "neutral.outlinedBorder",
        boxShadow: "lg",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "grid",
          // Single column on mobile; sidebar + content on desktop.
          gridTemplateColumns: { xs: "1fr", md: "340px 1fr", lg: "380px 1fr" },
        }}
      >
        {/* Sidebar: brand, greeting and progress */}
        <Stack
          rowGap={3}
          sx={{
            p: { xs: 2.5, sm: 3, md: 4 },
            bgcolor: { md: "background.level1" },
            borderBottom: { xs: "1px solid", md: "none" },
            borderRight: { md: "1px solid" },
            borderColor: "neutral.outlinedBorder",
          }}
        >
          {/* Brand header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" columnGap={1.25} alignItems="center">
              <FinanceableLogo height={24} />
            </Stack>
            <Pill intent="success" variant="soft">
              <Stack direction="row" columnGap={0.5} alignItems="center">
                <VerifiedUserOutlinedIcon sx={{ fontSize: "0.9rem" }} />
                Secure
              </Stack>
            </Pill>
          </Stack>

          {/* Greeting */}
          <Box>
            <Typography level="h3" bold>
              Hi {firstName},
            </Typography>
            <Typography level="body-sm" textColor="text.secondary" style={{ marginTop: "4px" }}>
              {config.introText}
            </Typography>
          </Box>

          {/* Progress */}
          <Box
            sx={{
              bgcolor: "background.surface",
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "neutral.outlinedBorder",
              p: 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography level="title-sm" bold>
                {uploadedCount} of {totalCount} uploaded
              </Typography>
              <Typography level="body-sm" textColor="primary.plainColor" bold>
                {isComplete ? "All done" : `${remaining} to go`}
              </Typography>
            </Stack>
            <ProgressBar value={percent} status={isComplete ? "green" : undefined} />
          </Box>
        </Stack>

        {/* Content: documents and actions */}
        <Stack rowGap={3} sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          {/* Requested documents — grows to fill the taller card, keeping the
              actions below anchored near the bottom on desktop. */}
          <Stack rowGap={2} sx={{ flex: { md: 1 } }}>
            <Typography level="body-xs" bold textColor="text.tertiary" style={{ letterSpacing: "0.06em" }}>
              REQUESTED DOCUMENTS
            </Typography>
            <Box
              sx={{
                display: "grid",
                // Two columns once there is room for them.
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {[...requestedDocuments, ...extraDocuments].map((doc) => (
                <DocumentRow
                  key={doc.key}
                  doc={doc}
                  isUploading={uploadingKey === doc.key}
                  isDeleting={deletingKey === doc.key}
                  isDisabled={!!busyKey && busyKey !== doc.key}
                  accept={fileConfig.accept}
                  allowedFileTypes={fileConfig.allowedFileTypes}
                  maxFileSize={fileConfig.maxFileSize}
                  helperText={fileConfig.helperText}
                  fileTypeErrorMessage={fileConfig.fileTypeErrorMessage}
                  fileSizeErrorMessage={fileConfig.fileSizeErrorMessage}
                  onUpload={handle.uploadDocument}
                  onDelete={handle.deleteDocument}
                />
              ))}
            </Box>
          </Stack>

          {showAdditionalDocuments && (
            <Box
              sx={{
                bgcolor: "background.level1",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Typography level="title-sm" bold>
                Have something else to share?
              </Typography>
              <Typography level="body-xs" textColor="text.tertiary" style={{ marginBottom: "12px" }}>
                Add any other document you think might help your application.
              </Typography>
              <Stack direction="row" columnGap={1.5} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Input
                    size="sm"
                    placeholder="e.g. Letter of employment"
                    value={customLabel}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCustomLabel(event.target.value)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handle.addCustomDocument();
                      }
                    }}
                  />
                </Box>
                <Button variant="secondary" size="sm" onClick={handle.addCustomDocument} disabled={!customLabel.trim()}>
                  Add
                </Button>
              </Stack>
            </Box>
          )}

          {/* Status bar */}
          {isSubmitted ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              columnGap={1.25}
              sx={{
                borderRadius: "10px",
                bgcolor: "success.softBg",
                color: "success.softColor",
                px: 2,
                py: 1.5,
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: "1.25rem", color: "success.solidBg" }} />
              <Typography level="body-sm" bold textColor="success.softColor">
                All documents have been submitted. Thank you!
              </Typography>
            </Stack>
          ) : (
            <Button
              variant="primary"
              size="lg"
              disabled={!isComplete || isSubmitting}
              loading={isSubmitting}
              onClick={handle.submitDocuments}
              style={{ width: "100%" }}
            >
              {isComplete
                ? "Submit documents"
                : `${remaining} ${remaining === 1 ? "document" : "documents"} still needed`}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>,
  );
}

export default function CustomerDocumentsUpload() {
  return <DocumentRequestUpload entityType="customer" />;
}
